import { Image } from "react-native"

import { useQuery } from "@tanstack/react-query"

import { H3, H7, Small } from "@/components/standard/BFText"
import { loanDashboardQueryOptions } from "@/queries/loan-dashboard"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { BFPressable } from "@/components/buttons/CommonButtons"
import { cn } from "@/lib/utils"
import { useLoanDashboardLoanListModal } from "./LoanDashboardLoanListModal"

interface LoanDashboardHeaderProps {
  loanId: string
}

interface ApplicationStagePillProps {
  timelineStage: {
    label: string
  }
}

function ApplicationStagePill({ timelineStage }: ApplicationStagePillProps) {
  const { onShow } = useLoanDashboardLoanListModal()
  return (
    <BFPressable
      title="Dashboard Timeline Stage"
      testID="dashboard-timeline-stage"
      onPress={() => {
        onShow()
      }}
    >
      {({ pressed }) => {
        return (
          <Horizontal
            className={cn(
              "items-center justify-center border border-beige bg-beige p-1.5 h-[15px]",
              pressed && "opacity-50"
            )}
          >
            <H7 className="leading-[16px] uppercase h-[16px]">
              {timelineStage.label}
            </H7>
          </Horizontal>
        )
      }}
    </BFPressable>
  )
}

function UpperHeader({ imageUri }: UpperHeaderProps) {
  return (
    <>
      <Horizontal className="pt-2.5 pb-2 justify-between items-center">
        <Horizontal className="h-[50px] justify-start items-center">
          <Image
            className="overflow-hidden rounded-full"
            source={{ uri: imageUri || "" }}
            resizeMode="cover"
            height={50}
            style={{ height: 50, width: 50 }}
          />
        </Horizontal>
      </Horizontal>
    </>
  )
}

export function LoanDashboardHeader({ loanId }: LoanDashboardHeaderProps) {
  const headerQuery = useQuery({
    ...loanDashboardQueryOptions(loanId),
    select: (data) => {
      return {
        streetAddress: `${data?.property?.street!}`.trim(),
        cityStateZip:
          `${data?.property?.city}, ${data?.property?.state} ${data?.property?.zipcode}`.trim(),
        timelineStage: data?.timelineStage!,
        imageUri: data?.property?.heroImage || undefined,
      }
    },
  })

  if (!headerQuery.data) {
    return null
  }

  const { streetAddress, cityStateZip, timelineStage, imageUri } =
    headerQuery.data

  return (
    <Vertical className={"min-h-32"}>
      <UpperHeader imageUri={imageUri} />
      <Horizontal className="flex-1 pb-2 border-b border-light-grey">
        <Horizontal className="justify-between flex-1">
          <Vertical>
            <H3>{streetAddress}</H3>
            <Small className="text-dark-grey">{cityStateZip}</Small>
          </Vertical>
          <Vertical className="items-end justify-end py-1 gap-2.5">
            <ApplicationStagePill timelineStage={timelineStage} />
          </Vertical>
        </Horizontal>
      </Horizontal>
    </Vertical>
  )
}

interface UpperHeaderProps {
  imageUri: string | undefined
}
